/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import React, { useEffect, useMemo, useState } from 'react';

import { TextField as MuiTextField, TextFieldProps as MuiTextFieldProps, formLabelClasses, inputBaseClasses, outlinedInputClasses, styled } from '@mui/material';
import { IFormState, IFieldInputProps, useFormErrorBoundary } from '@ngt/forms-core';
import { IAdditionalFormInputProps } from './Input';
import useErrorStyling, { ErrorClassMapping, ErrorStylingType } from '../../../hooks/form/useErrorStyling';
import { NumericFormat, NumericFormatProps, NumberFormatValues, InputAttributes } from 'react-number-format';
import { FormError } from '../../../errors';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

type NumericProps = Partial<Omit<MuiTextFieldProps, 'type'>> & IFieldInputProps<number, any, FormError, number> & IAdditionalFormInputProps & { numberFormat: Omit<NumericFormatProps, 'format'> & { format?: (val: number | string | undefined) => number | string | undefined } }

export interface INumericProps extends Omit<NumericProps, 'onChange'> {
    onChange: (value: number | null | undefined) => void;
}

interface IInternalNumberFormatProps {
    onChange: (event: { target: { value: number | undefined } }) => void;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const StyledMuiTextField = styled(MuiTextField)(({theme, margin}) => ({
    marginTop: margin === 'normal' ?
        theme.spacing(2) :
        margin === 'dense' ?
            theme.spacing(1) :
            theme.spacing(0),
    marginBottom: margin === 'normal' ?
        theme.spacing(1) :
        margin === 'dense' ?
            theme.spacing(0.5) :
            theme.spacing(0),
    [`& .${inputBaseClasses.root}`]: {
        padding: theme.spacing(0)
    },
    [`& .${inputBaseClasses.input}`]: {
        padding: theme.spacing(2, 2)
    },
    [`& .${formLabelClasses.root}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(2)}) scale(1)`
    },
    [`& label.${formLabelClasses.focused}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(-1.25)}) scale(1)`
    },
    [`& label.${formLabelClasses.filled}`]: {
        transform: `translate(${theme.spacing(2.25)}, ${theme.spacing(-1.25)}) scale(1)`
    },
    [`& fieldset.${outlinedInputClasses.notchedOutline}`]: {
        padding: theme.spacing(0, 1),

        '& legend': {
            fontSize: '1em',
            '& span': {
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1)
            }
        }
    }
}))

/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const InternalNumberFormat = React.forwardRef<NumericFormatProps<InputAttributes>, IInternalNumberFormatProps>(({
    onChange,
    ...rest
}, ref) => {
    const onValueChange = React.useCallback((values: NumberFormatValues) => {
        onChange({
            target: {
                value: values.floatValue,
            },
        })
    }, [onChange]);

    return (
        <NumericFormat
            {...rest}
            getInputRef={ref}
            onValueChange={onValueChange}
            onChange={undefined}
        />
    );
});

const Numeric: React.FunctionComponent<INumericProps> = ({
    FieldInputProps: { state: { value }, actions: {  update, blur, focus, touch } },
    fullWidth,
    onChange,
    sx,
    inputProps: { onFocus: inputPropsFocus, onBlur: inputPropsBlur, ...inputProps } = {},
    hasErrors,
    errors,
    errorType,
    InputProps,
    numberFormat,
    ...rest
}) => {

    const [localValue, setLocalValue] = useState<number | null>(null)

    useEffect(() => {
        setLocalValue(value ?? null);
    }, [value, setLocalValue])

    const onChangeCombined = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {        
        if (onChange) {
            onChange(+event.target.value);
        }

        const newValue = !isNaN(+event.target.value) ? +event.target.value : undefined;
        setLocalValue(newValue ?? null);
        update(newValue, true, true, true);
    }, [onChange, update, setLocalValue]);

    const onFocusCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsFocus) {
            inputPropsFocus(event);
        }

        focus();
    }, [inputPropsFocus, focus]);

    const onBlurCombined = React.useCallback((event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (inputPropsBlur) {
            inputPropsBlur(event);
        }

        blur();
        touch();
    }, [inputPropsBlur, blur, touch]);

    const errorStyling = useErrorStyling(errorType, ErrorStylingType.Input);

    return (
        <StyledMuiTextField
            type="text"
            {...rest as any}
            fullWidth={fullWidth === undefined ? true : fullWidth}
            InputProps={{
                ...InputProps,
                inputComponent: InputProps?.inputComponent === undefined ? InternalNumberFormat : InputProps?.inputComponent
            }}
            inputProps={{
                ...inputProps,
                onBlur: onBlurCombined,
                onFocus: onFocusCombined,
                ...numberFormat
            }}
            value={localValue ?? NaN}
            onChange={onChangeCombined}
            sx={{...sx, ...errorStyling}}
            error={hasErrors}
            helperText={errors && errors.length > 0 && errors?.map((error, i) => (<span className={ErrorClassMapping[error.type!]} key={i}>{error.message}</span>))}
        />
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default Numeric;
